// Versione ottimizzata per mobile-first
import { useState, useEffect } from 'react';
import {
  Zap, Home, Shield, Smartphone, Paintbrush, FileCheck,
  Phone, Mail, MapPin, ChevronDown, CheckCircle2, Award, ArrowRight,
  ChevronLeft, ChevronRight, Menu, X
} from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  companyInfo,
  services,
  certifications,
  galleryImages,
  aboutText,
  submitContactForm
} from '../mock';
import CookieBanner from '../components/CookieBanner';
import PrivacyModal from '../components/PrivacyModal';
import SEO from '../components/SEO';
import { Checkbox } from '../components/ui/checkbox';

const iconComponents = {
  Zap,
  Home,
  Shield,
  Smartphone,
  Paintbrush,
  FileCheck
};

const CategoryCarousel = ({ category, images, className = "", height = "400px" }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' });

  const scrollPrev = () => {
    if (emblaApi) emblaApi.scrollPrev();
  };

  const scrollNext = () => {
    if (emblaApi) emblaApi.scrollNext();
  };

  return (
    <div
      className={`relative group overflow-hidden rounded-2xl ${className}`}
      style={{ height }}
    >
      {/* Background gradient for empty spaces */}
      <div className="absolute inset-0 bg-gradient-to-br from-industrial-800 via-industrial-900 to-industrial-800 z-0" />

      <div className="absolute top-4 left-4 z-20">
        <span className="bg-neon-amber text-industrial-900 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
          {category}
        </span>
      </div>

      <div className="overflow-hidden h-full" ref={emblaRef}>
        <div className="flex h-full">
          {images.map((image) => (
            <div key={image.id} className="flex-[0_0_100%] min-w-0 relative h-full flex items-center justify-center">
              <img
                src={image.url}
                alt={image.title}
                className="max-w-full max-h-full object-contain z-10"
              />
              {/* Overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-industrial-900 via-transparent to-transparent opacity-80 z-10" />
              <div className="absolute bottom-6 left-6 right-16 z-20 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                <span className="text-neon-amber text-xs font-bold tracking-wider uppercase mb-1 block">{image.subtitle}</span>
                <h3 className="text-white text-xl font-bold leading-tight">{image.title}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={scrollPrev}
        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 md:bg-black/30 hover:bg-neon-amber hover:text-industrial-900 text-white p-2 rounded-full backdrop-blur-sm transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 z-20"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={scrollNext}
        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 md:bg-black/30 hover:bg-neon-amber hover:text-industrial-900 text-white p-2 rounded-full backdrop-blur-sm transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 z-20"
      >
        <ChevronRight className="w-6 h-6" />
      </button>
    </div>
  );
};

const HomePage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    serviceType: '',
    message: '',
    privacyAccepted: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // First Name validation
    if (!formData.firstName.trim() || formData.firstName.length < 2) {
      errors.firstName = "Inserisci un nome valido (min 2 caratteri).";
    }

    // Last Name validation
    if (!formData.lastName.trim() || formData.lastName.length < 2) {
      errors.lastName = "Inserisci un cognome valido (min 2 caratteri).";
    }

    // Email validation (Mandatory and Format check)
    if (!formData.email.trim()) {
      errors.email = "L'indirizzo email è obbligatorio.";
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Inserisci un indirizzo email valido.";
    }

    // Phone validation (Optional)
    // No strict validation required as per user request (telefono facoltativo)

    // Message validation
    if (!formData.message.trim() || formData.message.length < 10) {
      errors.message = "Il messaggio deve essere di almeno 10 caratteri.";
    }

    if (!formData.privacyAccepted) {
      errors.privacyAccepted = "È necessario accettare la privacy policy.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, serviceType: value }));
  };

  const handlePrivacyChange = (checked) => {
    setFormData(prev => ({ ...prev, privacyAccepted: checked }));
    if (formErrors.privacyAccepted) {
      setFormErrors(prev => ({ ...prev, privacyAccepted: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Per favore correggi gli errori nel modulo.');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitContactForm(formData);
      if (result.success) {
        // Track successful form submission in Google Analytics
        if (window.gtag) {
          window.gtag('event', 'generate_lead', {
            'event_category': 'Engagement',
            'event_label': 'Contact Form',
            'service_type': formData.serviceType
          });
        }

        toast.success(result.message);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          serviceType: '',
          message: '',
          privacyAccepted: false
        });
      } else {
        throw new Error(result.message || 'Errore durante l\'invio');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.message || 'Errore nell\'invio del messaggio. Riprova più tardi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-industrial-900 text-slate-200 selection:bg-neon-amber selection:text-industrial-900 font-sans overflow-x-hidden w-full">
      <SEO />
      <CookieBanner />

      {/* Navbar Overlay */}

      {/* Navbar Overlay */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full ${scrolled || isMenuOpen ? 'bg-industrial-900/95 backdrop-blur-md border-b border-white/5 py-3' : 'bg-transparent py-5'}`}>
        <div className="container mx-auto px-4 md:px-6 flex justify-between items-center w-full">
          <div className="shrink-0 flex items-end gap-4 pb-1">
            <div className="h-20 w-20 md:h-24 md:w-24 overflow-hidden relative bg-industrial-900">
              <img 
                src="/logo.png" 
                alt="" 
                className="absolute inset-0 w-full h-[160%] object-cover object-top"
              />
            </div>
            <div className="flex flex-row gap-1.5 font-bold tracking-tighter leading-none mb-3">
              <span className="text-white text-2xl md:text-3xl">TECNO</span>
              <span className="text-neon-amber text-2xl md:text-3xl">DOMUS</span>
              <span className="text-white text-2xl md:text-3xl">360</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex space-x-8 text-sm font-medium tracking-wide">
            {['Chi Siamo', 'Servizi', 'Certificazioni', 'Galleria'].map((item) => (
              <button
                key={item}
                onClick={() => {
                  scrollToSection(item.toLowerCase().replace(' ', '-'));
                  setIsMenuOpen(false);
                }}
                className="text-slate-300 hover:text-white hover:text-neon-amber transition-colors uppercase text-xs"
              >
                {item}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <Button
              onClick={() => {
                scrollToSection('contatti');
                setIsMenuOpen(false);
              }}
              className="hidden sm:flex bg-neon-amber hover:bg-amber-600 text-industrial-900 font-bold rounded-full px-5 py-1 text-sm md:text-base"
            >
              Contattaci
            </Button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-white hover:text-neon-amber transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Overlay */}
        <div className={`lg:hidden absolute top-full left-0 right-0 bg-industrial-900 border-b border-white/5 transition-all duration-300 overflow-hidden ${isMenuOpen ? 'max-h-screen py-8 opacity-100' : 'max-h-0 py-0 opacity-0'}`}>
          <div className="flex flex-col items-center space-y-6">
            {['Chi Siamo', 'Servizi', 'Certificazioni', 'Galleria'].map((item) => (
              <button
                key={item}
                onClick={() => {
                  scrollToSection(item.toLowerCase().replace(' ', '-'));
                  setIsMenuOpen(false);
                }}
                className="text-xl font-medium text-slate-300 hover:text-neon-amber transition-colors uppercase tracking-widest"
              >
                {item}
              </button>
            ))}
            <Button
              onClick={() => {
                scrollToSection('contatti');
                setIsMenuOpen(false);
              }}
              className="sm:hidden bg-neon-amber hover:bg-amber-600 text-industrial-900 font-bold rounded-full px-8 py-6 text-lg"
            >
              Contattaci
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        className="relative h-screen flex items-center justify-center overflow-hidden w-full"
      >
        <div
          className="absolute inset-0 z-0"
          style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' }}
        >
          {/* Dark Overlay Gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-industrial-900/80 via-industrial-900/50 to-industrial-900 z-10" />
          <img
            src="https://images.unsplash.com/photo-1558002038-109177381792?q=75&w=1200&auto=format&fit=crop"
            alt=""
            role="presentation"
            loading="eager"
            className="w-full h-full object-cover opacity-60 scale-105"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-20 text-center">
          <div className="max-w-5xl mx-auto space-y-8 animate-fade-in-up">
            <div className="inline-block px-4 py-1.5 rounded-full border border-neon-amber/30 bg-neon-amber/10 backdrop-blur-sm mb-4">
              <span className="text-neon-amber text-xs font-bold tracking-widest uppercase">Innovazione & Design</span>
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-bold leading-tight tracking-tight text-white drop-shadow-2xl">
              Ristrutturazioni e <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-amber to-orange-500">Impianti Elettrici</span> a Roma
            </h1>
            <p className="text-xl md:text-2xl font-light text-slate-300 max-w-2xl mx-auto leading-relaxed">
              {companyInfo.slogan}
            </p>
            <div className="pt-8 flex flex-col md:flex-row items-center justify-center gap-4">
              <Button
                onClick={() => scrollToSection('contatti')}
                size="lg"
                className="bg-neon-amber hover:bg-amber-600 text-industrial-900 px-8 py-6 text-lg font-bold rounded-full shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_30px_rgba(245,158,11,0.5)] transition-all duration-300 w-full md:w-auto"
              >
                Inizia il Progetto{' '}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: '8px' }}
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Button>
              <Button
                onClick={() => scrollToSection('servizi')}
                variant="outline"
                size="lg"
                className="border-white/20 hover:bg-white/10 text-white px-8 py-6 text-lg rounded-full backdrop-blur-sm w-full md:w-auto"
              >
                Scopri i Servizi
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-6 h-6 text-slate-400 opacity-75" />
        </div>
      </section>

      {/* Chi Siamo Section - Dark Glass Card */}
      <section id="chi-siamo" className="py-24 bg-industrial-900 relative overflow-hidden w-full">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-neon-amber/5 blur-[100px] rounded-full pointer-events-none" />
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {aboutText.title}
              </h2>
              <div className="space-y-6 text-lg text-slate-400 leading-relaxed">
                {aboutText.content.map((paragraph, index) => (
                  <p key={index} className="border-l-2 border-neon-amber/50 pl-6">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-neon-amber to-purple-600 rounded-2xl blur-lg opacity-30"></div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 group">
                <img
                  src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?q=80&w=2069&auto=format&fit=crop"
                  alt="Elettricista al lavoro"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <p className="text-white font-semibold">Professionalità e Sicurezza al primo posto</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Servizi Section - Grid with Hover Effects */}
      <section id="servizi" className="py-24 bg-industrial-800/50 overflow-hidden w-full">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              I Nostri <span className="text-neon-amber">Servizi</span>
            </h2>
            <p className="text-slate-400 text-lg">
              Soluzioni d'avanguardia per ogni esigenza abitativa e industriale
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconComponents[service.icon];
              return (
                <Card
                  key={service.id}
                  className="glass-card hover:-translate-y-2 group cursor-pointer border-transparent"
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-industrial-700/50 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-neon-amber transition-colors duration-300">
                      <IconComponent className="w-7 h-7 text-neon-amber group-hover:text-industrial-900 transition-colors duration-300" />
                    </div>
                    <CardTitle className="text-xl text-white group-hover:text-neon-amber transition-colors">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-slate-400 text-base leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-neon-amber to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Galleria Modern Grid with Carousels */}
      <section id="galleria" className="py-24 bg-industrial-800/30">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center">
            Portfolio <span className="text-white/20">Lavori</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(galleryImages)
              .filter(([category, images]) => !(category === 'Automation' && (!images || images.length === 0)))
              .map(([category, images], index) => {
                // Bento Grid Logic: First and Fourth items are wide (2 cols)
                const isWide = index === 0 || index === 3;
                const spanClass = isWide ? 'md:col-span-2' : 'md:col-span-1';

                // Height based on category content - taller for portrait images
                const categoryHeights = {
                  'Bagni & Cucine': '450px',      // Mix of portrait/square
                  'Lighting': '500px',             // Many portrait images
                  'Restyling appartamenti': '480px', // Portrait images
                  'Spazi commerciali & uffici': '480px', // Same as Restyling
                  'Automazione': '350px'           // Landscape images
                };
                const height = categoryHeights[category] || '400px';

                return (
                  <CategoryCarousel
                    key={category}
                    category={category}
                    images={images}
                    className={spanClass}
                    height={height}
                  />
                );
              })}
          </div>
        </div>
      </section>

      {/* Certificazioni Section - Clean Layout */}
      <section id="certificazioni" className="py-24 bg-industrial-900 relative">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="md:w-1/3">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Certificazioni <br /> & <span className="text-neon-cyan">Standards</span>
              </h2>
              <p className="text-slate-400 text-lg mb-8">
                La conformità normativa non è un optional, è la base del nostro lavoro. Garantiamo sicurezza totale.
              </p>
              <div className="flex -space-x-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="w-12 h-12 rounded-full border-2 border-industrial-900 bg-industrial-700 flex items-center justify-center text-xs font-bold text-white">
                    ISO
                  </div>
                ))}
              </div>
            </div>
            <div className="md:w-2/3 grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
              {certifications.map((cert, index) => (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/5 hover:bg-white/10 transition-all duration-300"
                >
                  <Award className="w-10 h-10 text-neon-cyan mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">{cert.title}</h3>
                  <p className="text-slate-400 text-sm">{cert.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contatti Modern Form */}
      <section id="contatti" className="py-24 bg-industrial-900 relative overflow-hidden w-full">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-neon-amber/5 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-4xl mx-auto bg-industrial-800/50 backdrop-blur-xl rounded-3xl p-5 md:p-12 border border-white/5 shadow-2xl">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-5xl font-bold text-white mb-4">
                Parliamo del tuo <span className="text-neon-amber">Progetto</span>
              </h2>
              <p className="text-slate-400">Compila il form per una consulenza gratuita e senza impegno</p>
            </div>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-slate-300">Nome</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      placeholder="Mario"
                      className={`bg-industrial-900/50 border-industrial-700 text-white placeholder:text-industrial-600 focus:border-neon-amber focus:ring-neon-amber/20 h-12 ${formErrors.firstName ? 'border-red-500 focus:border-red-500' : ''}`}
                    />
                    {formErrors.firstName && <p className="text-red-500 text-xs mt-1">{formErrors.firstName}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-slate-300">Cognome</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      placeholder="Rossi"
                      className={`bg-industrial-900/50 border-industrial-700 text-white placeholder:text-industrial-600 focus:border-neon-amber focus:ring-neon-amber/20 h-12 ${formErrors.lastName ? 'border-red-500 focus:border-red-500' : ''}`}
                    />
                    {formErrors.lastName && <p className="text-red-500 text-xs mt-1">{formErrors.lastName}</p>}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="mario@example.com"
                    className={`bg-industrial-900/50 border-industrial-700 text-white placeholder:text-industrial-600 focus:border-neon-amber focus:ring-neon-amber/20 h-12 ${formErrors.email ? 'border-red-500 focus:border-red-500' : ''}`}
                  />
                  {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-300">Telefono</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+39 333 1234567"
                    className="bg-industrial-900/50 border-industrial-700 text-white placeholder:text-industrial-600 focus:border-neon-amber focus:ring-neon-amber/20 h-12"
                  />
                </div>
              </div>

              <div className="space-y-4 flex flex-col justify-between">
                <div className="space-y-2">
                  <Label htmlFor="serviceType" className="text-slate-300">Interesse</Label>
                  <Select onValueChange={handleServiceChange} value={formData.serviceType}>
                    <SelectTrigger className="bg-industrial-900/50 border-industrial-700 text-white h-12">
                      <SelectValue placeholder="Seleziona tipologia" />
                    </SelectTrigger>
                    <SelectContent className="bg-industrial-800 border-industrial-700 text-white">
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.title} className="focus:bg-industrial-700 focus:text-neon-amber">
                          {service.title}
                        </SelectItem>
                      ))}
                      <SelectItem value="Altro" className="focus:bg-industrial-700 focus:text-neon-amber">
                        Altro
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 h-full">
                  <Label htmlFor="message" className="text-slate-300">Messaggio</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    placeholder="Descrivi brevemente..."
                    className={`bg-industrial-900/50 border-industrial-700 text-white placeholder:text-industrial-600 focus:border-neon-amber focus:ring-neon-amber/20 min-h-[120px] resize-none ${formErrors.message ? 'border-red-500 focus:border-red-500' : ''}`}
                  />
                  {formErrors.message && <p className="text-red-500 text-xs mt-1">{formErrors.message}</p>}
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="flex items-start space-x-3 p-4 bg-industrial-900/30 rounded-xl border border-white/5">
                  <Checkbox
                    id="privacyAccepted"
                    checked={formData.privacyAccepted}
                    onCheckedChange={handlePrivacyChange}
                    className="mt-1 border-white/20 data-[state=checked]:bg-neon-amber data-[state=checked]:text-industrial-900"
                  />
                  <div className="grid gap-1.5 leading-none">
                    <label
                      htmlFor="privacyAccepted"
                      className="text-sm font-medium text-slate-300 leading-relaxed cursor-pointer"
                    >
                      Accetto la <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsPrivacyModalOpen(true);
                        }}
                        className="text-neon-amber hover:underline font-bold"
                      >
                        Privacy Policy
                      </button> e acconsento al trattamento dei dati personali per le finalità descritte.
                    </label>
                    {formErrors.privacyAccepted && (
                      <p className="text-red-500 text-xs">{formErrors.privacyAccepted}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="md:col-span-2 pt-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-neon-amber hover:bg-amber-600 text-industrial-900 h-14 text-lg font-bold rounded-xl shadow-lg hover:shadow-neon-amber/25 transition-all duration-300"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-industrial-900 border-t-transparent" />
                      Invio in corso...
                    </span>
                  ) : 'Invia Richiesta Preventivo'}
                </Button>
              </div>
            </form>

            <div className="mt-12 pt-8 border-t border-white/5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 text-center md:text-left">
              <a
                href={`tel:${companyInfo.phone}`}
                onClick={() => window.gtag && window.gtag('event', 'phone_click', { event_category: 'Contact', event_label: 'Phone Button' })}
                className="flex items-center justify-center md:justify-start gap-4 p-3 hover:bg-white/5 rounded-xl transition-colors group"
              >
                <div className="w-10 h-10 bg-industrial-900 rounded-full flex items-center justify-center text-neon-amber border border-white/10 group-hover:bg-neon-amber group-hover:text-industrial-900 transition-colors">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Telefono</p>
                  <p className="text-white font-medium flex items-center gap-2">
                    <span className="text-lg">🇮🇹</span> {companyInfo.phone}
                  </p>
                </div>
              </a>
              <a
                href={`mailto:${companyInfo.email}`}
                onClick={() => window.gtag && window.gtag('event', 'email_click', { event_category: 'Contact', event_label: 'Email Button' })}
                className="flex items-center justify-center md:justify-start gap-4 p-4 hover:bg-white/5 rounded-xl transition-colors group"
              >
                <div className="w-10 h-10 bg-industrial-900 rounded-full flex items-center justify-center text-neon-amber border border-white/10 group-hover:bg-neon-amber group-hover:text-industrial-900 transition-colors">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Email</p>
                  <p className="text-white font-medium">{companyInfo.email}</p>
                </div>
              </a>
              <div className="flex items-center justify-center md:justify-start gap-4 p-4 hover:bg-white/5 rounded-xl transition-colors">
                <div className="w-10 h-10 bg-industrial-900 rounded-full flex items-center justify-center text-neon-amber border border-white/10">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Sede</p>
                  <p className="text-white font-medium">{companyInfo.area}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Minimal */}
      <footer className="bg-black text-slate-400 py-12 border-t border-white/10">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <div className="mb-8 flex items-end justify-center gap-6">
            <div className="h-40 w-40 md:h-52 md:w-52 overflow-hidden relative bg-industrial-900">
              <img 
                src="/logo.png" 
                alt="" 
                className="absolute inset-0 w-full h-[160%] object-cover object-top"
              />
            </div>
            <div className="text-4xl md:text-6xl font-bold tracking-tighter flex items-center gap-3 mb-6">
              <span className="text-white">TECNO</span>
              <span className="text-neon-amber">DOMUS</span>
              <span className="text-white">360</span>
            </div>
          </div>
          <div className="flex justify-center items-center gap-6 mb-8 text-sm">
            <span className="flex items-center gap-2 hover:text-white transition-colors cursor-pointer"><CheckCircle2 className="w-4 h-4 text-neon-amber" /> Impianti Certificati</span>
            <span className="hidden md:inline text-slate-700">•</span>
            <span className="flex items-center gap-2 hover:text-white transition-colors cursor-pointer"><CheckCircle2 className="w-4 h-4 text-neon-amber" /> Assistenza 24/7</span>
          </div>
          <p className="text-sm">
            &copy; {new Date().getFullYear()} {companyInfo.name}. P.IVA 12345678901
          </p>
        </div>
      </footer>

      <PrivacyModal
        isOpen={isPrivacyModalOpen}
        onOpenChange={setIsPrivacyModalOpen}
      />
    </div>
  );
};

export default HomePage;
